import { Injectable } from '@angular/core';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  NotificationAlertTypeEnum,
  NotificationSettings,
  ProfileStateModel,
  UserInfoType,
  UserMarketingPreference,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, tap, throwError } from 'rxjs';
import { AddProgressBarStack, RemoveProgressBarStack } from '../actions/progress-bar.action';
import {
  AvailableConfirmationMethodsUpdate,
  GetProfile,
  NotificationSettingsUpdate,
  SetActiveOrgId,
  SetActiveRole,
  SetActiveThemeForUser,
  SetProfile,
  UpdateUserMarketingPreferencesInProfileState,
  UserProfileFetched,
  UserProfileUpdateFailed,
  UserProfileUpdated,
} from '../actions/user-profile.action';

import * as Sentry from '@sentry/angular';
import { WidgetsHideConditionsSelector } from '../services/hide-conditions-service/hide-conditions-repository';
import { HideConditionsService } from '../services/hide-conditions-service/hide-conditions.service';
import { MetadataService } from '../services/metadata-service/metadata-service.service';

@State<ProfileStateModel>({
  name: 'profile',
  defaults: {
    id: undefined,
    firstName: '',
    middleName: undefined,
    lastName: '',
    fullName: '',
    logo: '',
    email: '',
    emailVerified: false,
    dob: '',
    country: '',
    phoneNumber: '',
    phoneVerified: false,
    address1: '',
    address2: '',
    address3: '',
    address4: '',
    city: '',
    county: '',
    postalCode: '',
    phoneCode: undefined,
    custom_questions: undefined,
    flatName: undefined,
    propertyName: undefined,
    propertyNumber: undefined,
    userName: '',
    activeOrganisationKybStatus: '',
    activeOrganisationId: undefined,
    activeRole: undefined,
    settings: '',
    profileCompletionStatus: 'pending',
    kycVerificationStatus: 'pending',
    orgCounter: 0,
    extraAttributes: {},
    availableConfirmationMethods: [],
    activeTheme: '',
    marketingPreferences: {} as UserMarketingPreference,
    applicationReference: '',
    profileUpdatePermittedDate: '',
  },
})
@Injectable()
export class ProfileState {
  defaultNotificationSettings: NotificationSettings = {
    [NotificationAlertTypeEnum.DAILY_BALANCE]: {
      sms: false,
      email: false,
      push: false,
      pushNative: true,
    },
    [NotificationAlertTypeEnum.TRANSACTIONS_SUCCESS_AND_FAILURE_ALERTS]: {
      sms: false,
      email: false,
      push: false,
      pushNative: true,
    },
    [NotificationAlertTypeEnum.CONVERSION_SUCCESS_AND_FAILURE_ALERTS]: {
      sms: false,
      email: false,
      push: false,
      pushNative: true,
    },
    [NotificationAlertTypeEnum.ADD_BENEFICIARY_ALERTS]: {
      sms: false,
      email: false,
      push: false,
      pushNative: true,
    },
    [NotificationAlertTypeEnum.PAYMENTS_REQUEST_ALERTS]: {
      sms: false,
      email: false,
      push: false,
      pushNative: true,
    },
  };
  constructor(
    private metadataService: MetadataService,
    private alertHandlerService: AlertHandlerService,
    private store: Store,
    private hideConditionsService: HideConditionsService,
  ) {}

  @Selector()
  static getProfile(state: ProfileStateModel) {
    return state;
  }
  @Selector()
  static getProfilePicture(state: ProfileStateModel) {
    return state.logo;
  }

  @Selector()
  static getAvailableConfirmationMethods(state: ProfileStateModel) {
    return state.availableConfirmationMethods ?? [];
  }

  @Selector()
  static getMarketingPreferences(state: ProfileStateModel): UserMarketingPreference {
    return state.marketingPreferences ?? ({} as UserMarketingPreference);
  }

  @Action(GetProfile)
  fetchProfile(ctx: StateContext<ProfileStateModel>, action: GetProfile) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetUser' }));
    try {
      return this.metadataService.getUser().pipe(
        tap((userProfileData: UserInfoType) => {
          const currentProfile = ctx.getState();

          if (currentProfile.id !== userProfileData.id || currentProfile.email !== userProfileData.email) {
            Sentry.setUser({
              id: userProfileData.id,
              email: userProfileData.email,
            });
          }

          ctx.setState({
            id: userProfileData.id,
            firstName: userProfileData.firstName,
            middleName: userProfileData.middleName,
            lastName: userProfileData.lastName,
            fullName: userProfileData.fullName as string,
            logo: userProfileData.logo as string,
            email: userProfileData.email,
            emailVerified: userProfileData.emailVerified,
            dob: userProfileData.dob,
            country: userProfileData.country,
            phoneNumber: userProfileData.phoneNumber,
            phoneVerified: userProfileData.phoneVerified as boolean,
            address1: userProfileData.address1,
            address2: userProfileData.address2,
            address3: userProfileData.address3,
            address4: userProfileData.address4,
            city: userProfileData.city,
            county: userProfileData.county,
            postalCode: userProfileData.postalCode,
            phoneCode: userProfileData.phoneCode,
            custom_questions: userProfileData.custom_questions,
            flatName: userProfileData.flatName,
            propertyName: userProfileData.propertyName,
            propertyNumber: userProfileData.propertyNumber,
            userName: userProfileData.userName as string,
            activeOrganisationKybStatus: userProfileData.organisationKybStatus,
            activeOrganisationId: userProfileData.organisation,
            activeRole: userProfileData.role,
            orgList: userProfileData.orgList,
            orgCounter: userProfileData.orgCounter,
            settings: JSON.stringify(
              userProfileData.settings !== '{}' ? userProfileData.settings : this.defaultNotificationSettings,
            ),
            kycVerificationStatus: userProfileData?.kycVerificationStatus ?? 'pending',
            profileCompletionStatus: userProfileData?.profileCompletionStatus ?? 'pending',
            extraAttributes: userProfileData.extraAttributes,
            activeTheme: userProfileData?.theme ?? '',
            marketingPreferences: userProfileData.marketingPreferences,
            profileUpdatePermittedDate: userProfileData?.profileUpdatePermittedDate,
            applicationReference: userProfileData.applicationReference,
          });
          if (userProfileData.id) localStorage.setItem('lastLoggedInId', userProfileData.id);
          performance.mark('boot:profile-loaded');
          ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetUser' }));
          this.hideConditionsService.setHideConditionFromWidget(
            WidgetsHideConditionsSelector.VERIFICATION_LOTTIE_ANIMATION,
          );
          if (action.emitUserProfileFetched) ctx.dispatch(new UserProfileFetched());
        }),
      );
    } catch (err) {
      ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetUser' }));
      return throwError(() => err);
    }
  }

  @Action(SetActiveRole)
  setActiveRole(ctx: StateContext<ProfileStateModel>, action: SetActiveRole) {
    return ctx.patchState({
      activeRole: action.role,
    });
  }

  @Action(SetActiveOrgId)
  setActiveOrgId(ctx: StateContext<ProfileStateModel>, action: SetActiveOrgId) {
    // Set active org in local storage to persist choice and read later:
    localStorage.setItem('activeOrganisationId', action.organisationId);

    return ctx.patchState({
      activeOrganisationId: action.organisationId,
    });
  }

  @Action(SetProfile)
  fetchSetProfile(ctx: StateContext<ProfileStateModel>, action: SetProfile) {
    const state = ctx.getState();

    const userProfileData: UserInfoType = { ...action.payload };

    if (userProfileData?.dob && isNaN(Date.parse(userProfileData.dob))) {
      userProfileData.dob = state.dob;
    }

    // Filter data immutably
    const filteredData = Object.fromEntries(
      Object.entries(userProfileData).filter(([, value]) => value !== undefined),
    ) as UserInfoType;

    // Create the final updated state object immutably
    const updatedState = action?.shouldMergeWithState ? { ...state, ...filteredData } : filteredData;

    return this.metadataService.updateUserProfileData(action.payload).pipe(
      tap((response) => {
        if (response) {
          ctx.patchState({
            ...updatedState,
            profileUpdatePermittedDate: response?.profileUpdatePermittedDate,
            marketingPreferences: response.marketingPreferences,
          } as Partial<ProfileStateModel>);
          ctx.dispatch(new UserProfileUpdated());
        }
      }),
      catchError((err) => {
        ctx.dispatch(new UserProfileUpdateFailed());
        return throwError(() => err);
      }),
    );
  }

  @Action(NotificationSettingsUpdate)
  notificationSettingsUpdate(ctx: StateContext<UserInfoType>, action: NotificationSettingsUpdate) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      settings: JSON.stringify(action.payload),
    });
  }

  @Action(AvailableConfirmationMethodsUpdate)
  availableConfirmationMethodsUpdate(
    ctx: StateContext<ProfileStateModel>,
    action: AvailableConfirmationMethodsUpdate,
  ) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      availableConfirmationMethods: action.payload,
    });
  }
  @Action(SetActiveThemeForUser)
  setActiveThemeForUser(ctx: StateContext<ProfileStateModel>, action: SetActiveThemeForUser) {
    const payload = { themeName: action.themeName };
    try {
      return this.metadataService.setActiveThemeForUser(payload).pipe(
        tap(() => {
          const state = ctx.getState();
          ctx.setState({
            ...state,
            activeTheme: action.themeName,
          });
        }),
      );
    } catch (err) {
      return throwError(() => err);
    }
  }

  @Action(UpdateUserMarketingPreferencesInProfileState)
  updateUserMarketingPreferences(
    ctx: StateContext<ProfileStateModel>,
    action: UpdateUserMarketingPreferencesInProfileState,
  ) {
    const state = ctx.getState();

    ctx.patchState({
      ...state,
      marketingPreferences: action.marketingPreferences,
    });
  }
}
